import clsx from "clsx";
import { useRouter } from "next/router";
import {
  ComponentProps,
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { RightMenuButtonsMobileProps } from "@ui/components/navigation/Navigation/components/PrimaryNavigation/components/PrimaryNavigationMobile/PrimaryNavigationMobile";
import {
  PinnedNavItem,
  RightMenuButtonsDesktopProps,
} from "@ui/components/navigation/Navigation/components/PrimaryNavigation/components/PrimaryNavigationStandard/PrimaryNavigationStandard";
import { useComponentConfig } from "@ui/context/component-config-context";
import { useNavigationContext } from "@ui/context/navigation-context";
import useDefaults from "@ui/hooks/use-defaults";
import { useCommunityContext } from "@voxmedia/duet-context/context/community-context";
import { type NavItem } from "@voxmedia/duet-context/context/global-data-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";
import { useLoopNavContext } from "@voxmedia/duet-context/context/loop-nav-context";

import MinimalNav from "./components/MinimalNav/MinimalNav";
import NavigationDrawer from "./components/NavigationDrawer";
import PrimaryNavigation from "./components/PrimaryNavigation";
import StickyNav from "./components/StickyNav";
import * as styles from "./Navigation.css";

export type OpenDrawerFn = (options?: { initialSearchFocus?: boolean }) => void;
export type CloseDrawerFn = () => void;

export interface NavigationDrawerConfig {
  shortcutBar?: React.ReactElement;
  endSection?: React.ReactElement;
  background?: React.ReactElement;
  randomizeSearchPlaceholder?: string[];
}

export interface StickyNavProps {
  enable?: boolean;
  headline?: string;
  streamHeadline?: any;
  streamUrl?: string;
  packageHeadline?: string;
  packageUrl?: string;
  isLive?: boolean;
}

export interface NavigationBaseConfig {
  Logo: FunctionComponent<{ className?: string }>;
  PrimaryLogo?: FunctionComponent<{ className?: string }>;
  NotificationsLogo?: FunctionComponent<{ className?: string }>;
  drawerItems: NavItem[];
  getDrawerItems?: (origin: string, mainNav: NavItem[] | null) => NavItem[];
  getPinnedItems: (
    origin: string,
    mainNav: NavItem[] | null,
    communityDomain: string,
  ) => PinnedNavItem[];
  primaryNavigationComponent: FunctionComponent<
    ComponentProps<typeof PrimaryNavigation>
  >;
  linkMenu: {
    showLinkLines: boolean;
    linkClassName?: string;
  };
  rightMenuButtons: {
    desktop: React.FunctionComponent<RightMenuButtonsDesktopProps>;
    mobile: React.FunctionComponent<RightMenuButtonsMobileProps>;
  };
  MinimalLogo?: FunctionComponent<{ className?: string }>;
  cta: {
    desktop: React.ReactElement | null;
    mobile: React.ReactElement | null;
  };
  stickyNavComponent?: FunctionComponent<ComponentProps<typeof StickyNav>>;
  showStickyNavCommentCount?: boolean;
  showSubscribeInNav?: boolean;
  getDrawerComponents: (origin: string) => NavigationDrawerConfig;
  showAuth?: boolean;
  showStickyOnCommentCountClick?: boolean;
  stickyVisibleThroughBottom?: boolean;
  notificationsClass?: string;
  /**
   * When true, the minimal-nav layout renders a notifications bell to the
   * left of the hamburger (alongside `NotificationsLogo`). Brand-config
   * driven; currently Verge-only.
   */
  showNotificationsButton?: boolean;
}

type BaseNavigationVariants =
  | "standard"
  | "homepage"
  | "monochrome"
  | "monochrome-minimal";

export type VergeNavigationVariants =
  | "verge-black"
  | "verge-pernod"
  | "verge-blurple"
  | "verge-franklin"
  | BaseNavigationVariants;

export type NavigationVariants =
  | "minimal"
  | "branded"
  | VergeNavigationVariants;

type Props = Partial<NavigationBaseConfig> & {
  stickyNav?: StickyNavProps;
  variant: NavigationVariants;
  renderStickyNavCommentCount?: (className: string) => ReactNode;
  tagline?: string;
};

const Navigation: FunctionComponent<Props> = (props) => {
  const componentConfig = useComponentConfig("navigation");
  const {
    Logo,
    PrimaryLogo,
    drawerItems,
    getDrawerItems,
    getPinnedItems,
    MinimalLogo,
    NotificationsLogo,
    cta,
    stickyNav,
    getDrawerComponents,
    variant,
    showAuth,
    linkMenu,
    rightMenuButtons,
    showStickyNavCommentCount,
    showSubscribeInNav,
    primaryNavigationComponent: PrimaryNavigationComponent,
    stickyNavComponent,
    showStickyOnCommentCountClick,
    stickyVisibleThroughBottom,
    notificationsClass,
    showNotificationsButton,
  } = useDefaults(props, componentConfig);

  const { currentOrigin, currentCommunity } = useCommunityContext();
  const { loopNavData } = useLoopNavContext();
  const {
    setOpenDrawerFn,
    setCloseDrawerFn,
    navigationOverrides,
    setCommentCountClickFn,
  } = useNavigationContext();
  const { mainNav } = useGlobalDataContext();

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [overrideStickyVisible, setOverrideStickyVisible] = useState(false);
  const [initialSearchFocus, setInitialSearchFocus] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [activeVariant, setActiveVariant] = useState(variant);
  const [activeHeadline, setActiveHeadline] = useState(stickyNav?.headline);
  const openDrawer = useCallback<OpenDrawerFn>((options = {}) => {
    setInitialSearchFocus(Boolean(options.initialSearchFocus));
    setDrawerOpen(true);
  }, []);
  const router = useRouter();
  const closeDrawer = () => setDrawerOpen(false);
  const handleCommentCountClick = useCallback(() => {
    if (showStickyOnCommentCountClick) {
      setOverrideStickyVisible(true);
    }
  }, [showStickyOnCommentCountClick, setOverrideStickyVisible]);

  useEffect(() => {
    setOpenDrawerFn(openDrawer);
    setCloseDrawerFn(closeDrawer);
    setCommentCountClickFn(handleCommentCountClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationOverrides?.variant, router]);

  // Handle fade animation when navigationOverrides.variant changes (fades one nav into the next)
  useEffect(() => {
    const overrideVariant = navigationOverrides?.variant;
    if (!overrideVariant || activeVariant === overrideVariant) {
      return;
    }

    setFadeIn(true);
    setActiveVariant(overrideVariant === "none" ? variant : overrideVariant);

    setTimeout(() => {
      setFadeIn(false);
    }, 600);
  }, [activeVariant, navigationOverrides?.variant, variant]);

  useEffect(() => {
    const overrideHeadline = navigationOverrides?.headline;
    if (!overrideHeadline) {
      return;
    }

    setActiveHeadline(
      overrideHeadline === "none" ? stickyNav?.headline : overrideHeadline,
    );
  }, [navigationOverrides?.headline, stickyNav?.headline]);

  const drawer = useMemo(
    () => getDrawerComponents(currentOrigin),
    [getDrawerComponents, currentOrigin],
  );

  const pinnedItems = useMemo(
    () => getPinnedItems(currentOrigin, mainNav, currentCommunity.domain),
    [getPinnedItems, currentOrigin, mainNav, currentCommunity],
  );

  const items = useMemo(
    () =>
      getDrawerItems ? getDrawerItems(currentOrigin, mainNav) : drawerItems,
    [getDrawerItems, currentOrigin, drawerItems, mainNav],
  );

  const StickyNavComponent = stickyNavComponent || StickyNav;

  return (
    <>
      {activeVariant === "minimal" || activeVariant === "branded" ? (
        <MinimalNav
          Logo={Logo}
          NotificationsLogo={NotificationsLogo}
          isBranded={activeVariant === "branded"}
          showNotificationsButton={showNotificationsButton}
        />
      ) : (
        <div className={clsx(fadeIn && styles.fade)}>
          <PrimaryNavigationComponent
            PrimaryLogo={PrimaryLogo}
            Logo={Logo}
            items={pinnedItems}
            cta={cta}
            variant={activeVariant}
            showLinkLines={linkMenu.showLinkLines}
            linkClassName={linkMenu.linkClassName}
            rightMenuButtons={rightMenuButtons}
            showSubscribeInNav={showSubscribeInNav}
            tagline={props.tagline}
          />
        </div>
      )}
      <NavigationDrawer
        items={items}
        open={isDrawerOpen}
        onClose={closeDrawer}
        shortcutBar={drawer.shortcutBar}
        endSection={drawer.endSection}
        background={drawer.background}
        randomizeSearchPlaceholder={drawer.randomizeSearchPlaceholder}
        showAuth={showAuth}
        initialSearchFocus={initialSearchFocus}
      />
      {(stickyNav?.enable || loopNavData) && (
        <StickyNavComponent
          overrideStickyVisible={overrideStickyVisible}
          onSetOverrideStickyVisible={(visible: boolean) =>
            setOverrideStickyVisible(visible)
          }
          Logo={Logo}
          MinimalLogo={MinimalLogo}
          NotificationsLogo={NotificationsLogo}
          {...stickyNav}
          headline={activeHeadline}
          isBranded={activeVariant === "branded"}
          stickyVisibleThroughBottom={stickyVisibleThroughBottom}
          showStickyOnCommentCountClick={showStickyOnCommentCountClick}
          showNotificationsButton={showNotificationsButton}
          notificationsClass={notificationsClass}
          {...(showStickyNavCommentCount &&
            // FIXME: This is a kludge to hide the comment count when the override headline is set.
            (!navigationOverrides?.headline ||
              navigationOverrides.headline === "none") && {
              renderCommentCount: props.renderStickyNavCommentCount,
            })}
        />
      )}
    </>
  );
};

export type NavigationComponent = typeof Navigation;
export default Navigation;
